import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, ShoppingBag, MessageSquare, CheckCheck } from "lucide-react";
import { api, fmtDateTime } from "../lib/api";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const ICONS = {
  new_order: ShoppingBag,
  order_status: ShoppingBag,
  new_message: MessageSquare,
};

export function NotificationBell() {
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const prevUnread = useRef(0);

  const loadUnread = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/unread");
      setUnread(data.count || 0);
      prevUnread.current = data.count || 0;
    } catch { /* non authentifié : ignore */ }
  }, []);

  const loadItems = useCallback(async () => {
    try { const { data } = await api.get("/notifications"); setItems(data); } catch { /* */ }
  }, []);

  useEffect(() => {
    loadUnread();
    const t = setInterval(loadUnread, 10000);
    return () => clearInterval(t);
  }, [loadUnread]);

  useEffect(() => { if (open) loadItems(); }, [open, loadItems]);

  const onClickItem = async (n) => {
    try { if (!n.read) await api.post(`/notifications/${n.notification_id}/read`); } catch { /* */ }
    setOpen(false);
    loadUnread();
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try { await api.post("/notifications/read-all"); } catch { /* */ }
    setUnread(0);
    loadItems();
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative p-2 text-slate-600 transition-colors duration-200 hover:text-ciel">
          <Bell size={20} strokeWidth={1.8} />
          {unread > 0 && (
            <span data-testid="notification-count" className="absolute -right-0.5 -top-0.5 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 font-mono text-[10px] font-semibold text-white">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0" data-testid="notification-panel">
        <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
          <span className="text-sm font-semibold text-navy">Notifications</span>
          {items.some((n) => !n.read) && (
            <button onClick={markAll} data-testid="notification-mark-all" className="flex items-center gap-1 text-[11px] font-medium text-ciel hover:underline">
              <CheckCheck size={13} /> Tout marquer lu
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {items.length === 0 ? (
            <p className="px-4 py-10 text-center text-xs text-slate-400">Aucune notification.</p>
          ) : items.map((n) => {
            const Icon = ICONS[n.ntype] || Bell;
            return (
              <button key={n.notification_id} data-testid={`notification-item-${n.notification_id}`}
                onClick={() => onClickItem(n)}
                className={`flex w-full items-start gap-3 border-b border-slate-50 px-4 py-3 text-left transition-colors hover:bg-slate-50 ${n.read ? "" : "bg-ciel/5"}`}>
                <span className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${n.read ? "bg-slate-100 text-slate-500" : "bg-ciel/15 text-ciel"}`}>
                  <Icon size={15} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-[13px] font-semibold text-navy">{n.title}</span>
                  <span className="block truncate text-[12px] text-slate-500">{n.body}</span>
                  <span className="block text-[10px] text-slate-400">{fmtDateTime(n.created_at)}</span>
                </span>
                {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-red-500" />}
              </button>
            );
          })}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
